"use client";

// src/components/organisms/legal/posthog-consent.tsx
// Same gate as <GoogleAnalyticsConsent>, for posthog-js: capture runs unless
// the visitor has rejected the "analytics" cookie category (opt-out — see
// IMPLIED_CONSENT in lib/consent.ts). Mounted in the root layout, so a
// rejection made on a marketing page also silences PostHog on app surfaces,
// which never show the banner.

import { usePathname } from "next/navigation";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  type AnalyticsConsent,
  type ConsentAction,
  CONSENT_CHANGED_EVENT,
  consentAction,
  readAnalyticsConsent,
} from "@/lib/consent";
import {
  applyReplayPolicy,
  resumePostHog,
  startPostHog,
  stopPostHog,
} from "@/lib/posthog/client";
import { listenForClientErrors } from "@/lib/posthog/error-events";

type PostHogConsentDeps = {
  start: () => void;
  stop: () => void;
  resume: () => void;
  replay: (pathname: string) => void;
};

/**
 * Pure decision the effect below applies: maps `consentAction`'s result to
 * the one vendor call it implies. A rejection (`analytics === false`) must
 * clear PostHog's cookies and storage even when nothing started on this
 * surface, so `consentAction`'s "none" is not the last word here — a
 * rejection always stops. `load` and `resume` also apply the replay policy
 * for the current surface, since starting or resuming capture is exactly
 * when session recording (dis)allowance can change too.
 */
export function applyPostHogConsent(
  analytics: AnalyticsConsent,
  started: boolean,
  pathname: string,
  deps: PostHogConsentDeps = {
    start: startPostHog,
    stop: stopPostHog,
    resume: resumePostHog,
    replay: applyReplayPolicy,
  },
): { action: ConsentAction; started: boolean } {
  const action = consentAction(analytics, started);
  switch (action) {
    case "load":
      deps.start();
      deps.replay(pathname);
      return { action, started: true };
    case "resume":
      deps.resume();
      deps.replay(pathname);
      return { action, started };
    case "stop":
      deps.stop();
      return { action, started };
    case "none":
      if (analytics === false) deps.stop();
      return { action, started };
  }
}

export function PostHogConsent() {
  // Seeded SYNCHRONOUSLY, during the first render, and that is the whole
  // point: with `useState(null)` `consentAction(null, false)` is "none", so
  // `startPostHog()` could only run one render round-trip later — after the
  // mount effect below had set the state — and by then every other mount
  // effect in the tree has already run. A page that captures on mount (the
  // KYB wizard) would call `capture()` while `posthog.__loaded` is still
  // false, so without this seed a cold load drops its first event silently
  // (client.ts `isActive`). The decision still comes from
  // `readAnalyticsConsent()` and nothing else. On the server it reads no
  // cookie and returns the implied default; the component renders `null`, so
  // there is nothing to mismatch on hydration, and the hydration render reads
  // the real cookie.
  const [analytics, setAnalytics] = useState<AnalyticsConsent>(readAnalyticsConsent);
  const startedRef = useRef(false);
  const pathname = usePathname();
  // The consent effect reads the current path from a ref so that a navigation
  // never re-runs it: `consentAction(true, true)` is "resume", and
  // `opt_in_capturing()` captures a `$opt_in` event each time it is called
  // (meant for a re-acceptance only). Navigation is the layout effect's job.
  const pathnameRef = useRef(pathname);

  useEffect(() => {
    function sync() {
      setAnalytics(readAnalyticsConsent());
    }
    sync();
    window.addEventListener(CONSENT_CHANGED_EVENT, sync);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, sync);
  }, []);

  // `client_error` with the error name only; `capture` is a no-op
  // until PostHog is active, so this can listen from the first mount.
  useEffect(() => listenForClientErrors(window), []);

  useEffect(() => {
    // Not moved to a render-time adjustment (the usual
    // react-hooks/set-state-in-effect fix): consentAction is a pure function
    // of (analytics, startedRef.current), so setting startedRef.current here
    // changes what the *next* evaluation of this same switch would return.
    // Flipping it during render, before this Effect runs, would make
    // consentAction see started=true on this same pass and return "resume"
    // instead of "load" — calling resumePostHog() immediately after
    // startPostHog(), before the SDK has actually initialised.
    startedRef.current = applyPostHogConsent(
      analytics,
      startedRef.current,
      pathnameRef.current,
    ).started;
  }, [analytics]);

  // A `useLayoutEffect`, not `useEffect` — the replay policy for the
  // new pathname must be applied (and, on a blocked path, recording stopped)
  // before rrweb's own effect can observe the new page's first paint.
  // Layout effects run before passive effects, so the ref is current by the
  // time the consent effect reads it on the first render.
  useLayoutEffect(() => {
    pathnameRef.current = pathname;
    if (!startedRef.current) return;
    applyReplayPolicy(pathname);
  }, [pathname]);

  return null;
}
